import { useCallback, useState } from "react"
import toast from 'react-hot-toast'
import { sendItemizedBillNow } from "@healthshare/shared/commands/expenses"
import { useBackendApi } from "../hooks/useBackendApi"
import { useConfirm } from "../providers/confirmDialog"
import { useSentry } from "../providers/sentry"

interface SendNowButtonProps {
  expenseId: string
  onSend: () => void
}

export function SendNowButton({ expenseId, onSend }: SendNowButtonProps) {
  const api = useBackendApi()
  const [showConfirm] = useConfirm()
  const sentry = useSentry()
  const [isSendingItemizedBillRequest, setIsSendingItemizedBillRequest] = useState(false)

  const handleSendItemizedBillRequest = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();

    const confirmed = await showConfirm({
      title: 'Send Itemized Bill Request',
      message: 'Send itemized bill request now? This will email the provider requesting an itemized bill for this expense.',
      actions: [
        {
          title: 'Cancel',
          style: 'secondary',
          isCancel: true,
        },
        {
          title: 'Send Now',
          style: 'primary',
          isConfirm: true,
        }
      ]
    })

    if (!confirmed) {
      return
    }

    setIsSendingItemizedBillRequest(true)

    try {
      await sendItemizedBillNow(api, expenseId)
      toast.success('Itemized bill request sent successfully')

      // Add a small delay to ensure database consistency before reloading conversations
      setTimeout(async () => {
        await onSend()
      }, 500)
    } catch (error) {
      console.error('Error sending itemized bill request:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'

      // Report exception to Sentry
      sentry.captureException(error, {
        tags: {
          component: 'SendNowButton',
          action: 'send_itemized_bill_request'
        },
        extra: {
          expenseId
        }
      })

      toast.error(`Failed to send itemized bill request: ${errorMessage}`)
    } finally {
      setIsSendingItemizedBillRequest(false)
    }
  }, [expenseId, api, onSend, showConfirm, sentry])

  return (
    <button 
      className="btn btn-link btn-sm text-primary ms-2 p-0" 
      onClick={handleSendItemizedBillRequest}
      disabled={isSendingItemizedBillRequest}
      style={{ fontSize: '14px', textDecoration: 'none', fontWeight: '500' }}
    >
      {isSendingItemizedBillRequest ? (
        <>
          <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle' }}>hourglass_empty</i>
          &nbsp;Sending...
        </>
      ) : (
        <>
          <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle' }}>send</i>
          &nbsp;SEND NOW
        </>
      )}
    </button>
  )
}